import { useEffect, useState, useCallback, useContext } from "react";
import StaffNav from "./managernav";
import React from "react";
import { format } from "date-fns";
import { ChartBarIcon, CurrencyPoundIcon, CalendarDaysIcon, ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { VictoryPie, VictoryContainer, VictoryTooltip } from 'victory';
import { AuthContext } from "../AuthContext";


export default function Shifts() {
  const [departments, setDepartments] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [departmentBudgets, setDepartmentBudgets] = useState({});
  const [monthlyBudget, setMonthlyBudget] = useState(0);
  const [bugetModalOpen, setBugetModalOpen] = useState(false);
  const [reportError, setReportError] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [openDaysPerWeek, setOpenDaysPerWeek] = useState(7);
  const [staffList, setStaffList] = useState([]);
  const [vacancyModalOpen, setVacancyModalOpen] = useState(false);
  const [vacancyForm, setVacancyForm] = useState({
    role: "",
    jobs: "",
    description: "",
    skills: "",
    date: "",
    assessmentCode: "",
  });
  const [vacancyError, setVacancyError] = useState(false);
  const [vacancySuccess, setVacancySuccess] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [assessmentsError, setAssessmentsError] = useState("");
  const [expenditure, setExpenditure] = useState({
    daily: 0,
    monthly: {},
    yearly: 0
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);


const user = useContext(AuthContext);

  const safeText = (value, fallback = 'Unassigned') => {
    const normalized = (value ?? '').toString().trim();
    return normalized || fallback;
  };

  const isZeroHoursContract = (contractType = '') => {
    const normalized = safeText(contractType, '').toLowerCase();
    return normalized === '0 hours' || normalized === '0 hour' || normalized === '0' || normalized.includes('zero hour');
  };

  const getWeeklyAccruedHours = (staffMember = {}) => {
    if (isZeroHoursContract(staffMember?.ContractType)) {
      const averageHours = Number(staffMember?.AverageHours) || 0;
      // For zero-hours contracts, AverageHours is already a weekly total.
      return averageHours;
    }

    // ContractedHours is stored as a weekly total for standard contracts.
    return Number(staffMember?.ContractedHours) || 0;
  };

  const shellBg = "min-h-screen bg-[radial-gradient(circle_at_top_left,_#f0f9ff_0%,_#eef2ff_40%,_#f8fafc_100%)]";
  const glassCard = "rounded-2xl border border-indigo-100/80 bg-white/85 p-6 shadow-[0_18px_38px_rgba(30,41,59,0.09)] backdrop-blur-xl";
  const tableShell = "rounded-2xl border border-slate-200 bg-white shadow-[0_16px_34px_rgba(15,23,42,0.08)]";




  // Fetch departments and budgets
  const fetchDepartments = async () => {
    try {
      const response = await fetch("https://api.insightos.co.uk/api/teambudgets", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch departments");
      const data = await response.json();
      setDepartments(data.data || []);
      if (data.data) {
        const initialBudgets = {};
        data.data.forEach((dept) => {
          initialBudgets[dept.Name] = dept.MonthlyBudget || 0;
        });
        setDepartmentBudgets(initialBudgets);
      }
      if (data.budget && data.budget.length > 0) {
        setMonthlyBudget(Number(data.budget[0].Budget));
        setOpenDaysPerWeek(Number(data.budget[0].DaysOpen));
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };


  // Fetch staff
  const fetchStaff = async () => {
    try {
      const responseStaff = await fetch("https://api.insightos.co.uk/api/staff", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const today = new Date();
      const todaysDate = format(today, 'yyyy-MM-dd');

      const response5 = await fetch(`https://api.insightos.co.uk/api/dailyexpenditure/${todaysDate}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response5.ok) throw new Error('Failed to fetch daily expenditure');

        const data5 = await response5.json();

        console.log(data5);

        setExpenditure({
          daily: data5.daily || 0,
          monthly: data5.monthly || {},
          yearly: data5.yearly || 0
        });



      if (!responseStaff.ok) throw new Error("Failed to fetch staff");
      const dataStaff = await responseStaff.json();
      setStaffList(dataStaff.data || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch("https://api.insightos.co.uk/api/allroles", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch roles");

      const data = await response.json();
      const roles = Array.isArray(data.data) ? data.data : [];
      setRoleTemplates(roles);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoleTemplates([]);
    }
  };

  const loadAssessments = useCallback(async () => {
    setAssessmentsLoading(true);
    setAssessmentsError("");
    try {
      const response = await fetch("https://analytics.insightos.co.uk/assesment/list", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.analyticsToken}`,

        },
      });
      if (!response.ok) throw new Error("Failed to fetch assessments");
      const data = await response.json();
      setAssessments(Array.isArray(data.data) ? data.data : []);
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setAssessments([]);
      setAssessmentsError("Unable to load assessments right now.");
    } finally {
      setAssessmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchStaff();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (vacancyModalOpen) {
      loadAssessments();
    }
  }, [vacancyModalOpen, loadAssessments]);

  // Group staff by department
  const groupedStaffByDepartment = () => {
    const staffByDept = {};
    staffList.forEach((staff) => {
      const dept = safeText(staff?.Department);
      if (!staffByDept[dept]) staffByDept[dept] = [];
      staffByDept[dept].push(staff);
    });
    return staffByDept;
  };

  // Calculate cost per department for this month using contracted hours or average hours for 0 Hours contracts
  const departmentCosts = {};
  const departmentHours = {};
  const groupedDept = groupedStaffByDepartment();
  Object.entries(groupedDept).forEach(([dept, deptStaff]) => {
    const cleanDept = safeText(dept);
    let total = 0;
    let hours = 0;
    deptStaff.forEach((staff) => {
      const hoursToUse = getWeeklyAccruedHours(staff);

      console.log(`Staff: ${staff.FirstName} ${staff.LastName}, Role: ${staff.Role}, ContractType: ${staff.ContractType}, HoursToUse: ${hoursToUse}, AverageHours: ${staff.AverageHours}, ContractedHours: ${staff.ContractedHours}, Department: "${staff.Department}"`);
    
      const hourlyRate = Number(staff.HourlyRate) || 0;
      const monthlyHours = hoursToUse * (52 / 12);
      total += monthlyHours * hourlyRate;
      hours += monthlyHours;
    });
    departmentCosts[cleanDept] = (departmentCosts[cleanDept] || 0) + total;
    departmentHours[cleanDept] = (departmentHours[cleanDept] || 0) + hours;
  });

  console.log(departmentCosts);
  console.log(departmentHours);
  // Calculate average daily cost per department (for this month)
  const workingDaysInMonth = openDaysPerWeek * (52 / 12);
  const departmentAvgDaily = {};
  Object.entries(departmentCosts).forEach(([dept, cost]) => {
    const cleanDept = safeText(dept);
    departmentAvgDaily[cleanDept] = workingDaysInMonth > 0 ? cost / workingDaysInMonth : 0;
  });

  // Calculate yearly projections per department
  const workingDaysInYear = openDaysPerWeek * 52;
  const departmentYearly = {};
  Object.entries(departmentAvgDaily).forEach(([dept, avgDaily]) => {
    const cleanDept = safeText(dept);
    departmentYearly[cleanDept] = avgDaily * workingDaysInYear;
  });

  // Calculate remaining budget per department
  const departmentBudgetLeft = {};
  Object.entries(departmentBudgets).forEach(([dept, budget]) => {
    const cleanDept = safeText(dept);
    departmentBudgetLeft[cleanDept] = (budget || 0) - (departmentCosts[cleanDept] || 0);
  });

  console.log("Department Budget Left:", departmentBudgetLeft);

  // Helper for color
  const getBudgetColor = (val) => (val >= 0 ? "text-green-600" : "text-red-600");

  // Role-based: For each Department+Role combination, show HourlyRate and available hours
  const rolesByDepartment = {};
  staffList.forEach((staff) => {
    const dept = safeText(staff?.Department);
    const role = safeText(staff?.Role, 'Unassigned Role');
    const deptRoleKey = `${dept}-${role}`;
    if (!rolesByDepartment[dept]) rolesByDepartment[dept] = {};
    if (!rolesByDepartment[dept][deptRoleKey]) {
      rolesByDepartment[dept][deptRoleKey] = {
        role: role,
        hourlyRate: Number(staff.HourlyRate) || 0
      };
    } else {
      // Take the minimum hourly rate for this dept-role combination
      rolesByDepartment[dept][deptRoleKey].hourlyRate = Math.min(
        rolesByDepartment[dept][deptRoleKey].hourlyRate,
        Number(staff.HourlyRate) || 0
      );
    }
  });

  // --- Company-wide roles for vacancy dropdown ---
  const allUniqueRoles = [...new Set(
    [
      ...roleTemplates.map((role) => role?.Name),
      ...staffList.map((staff) => staff?.Role),
    ]
      .map((roleName) => safeText(roleName, ''))
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  const roleRateByName = roleTemplates.reduce((acc, role) => {
    const roleName = safeText(role?.Name, '');
    if (!roleName) return acc;

    const rate = Number(role?.PayRate);
    if (Number.isFinite(rate) && rate > 0) {
      acc[roleName] = rate;
    }
    return acc;
  }, {});

  const globalRoleMinRateByName = staffList.reduce((acc, staff) => {
    const roleName = safeText(staff?.Role, '');
    if (!roleName) return acc;

    const staffRate = Number(staff?.HourlyRate);
    if (!Number.isFinite(staffRate) || staffRate <= 0) return acc;

    if (!acc[roleName]) {
      acc[roleName] = staffRate;
    } else {
      acc[roleName] = Math.min(acc[roleName], staffRate);
    }

    return acc;
  }, {});

  const allDepartmentNames = [...new Set(
    [
      ...departments.map((dept) => safeText(dept?.Name, '')),
      ...Object.keys(departmentBudgetLeft).map((name) => safeText(name, '')),
      ...Object.keys(rolesByDepartment).map((name) => safeText(name, '')),
    ].filter(Boolean)
  )].sort((a, b) => a.localeCompare(b));

  const getRoleRateForDepartment = (roleName, departmentName) => {
    const deptRoleKey = `${departmentName}-${roleName}`;
    const roleRateFromDeptStaff = Number(rolesByDepartment?.[departmentName]?.[deptRoleKey]?.hourlyRate);
    if (Number.isFinite(roleRateFromDeptStaff) && roleRateFromDeptStaff > 0) {
      return roleRateFromDeptStaff;
    }

    const roleRateFromTemplate = Number(roleRateByName[roleName]);
    if (Number.isFinite(roleRateFromTemplate) && roleRateFromTemplate > 0) {
      return roleRateFromTemplate;
    }

    const roleRateFromGlobalStaff = Number(globalRoleMinRateByName[roleName]);
    if (Number.isFinite(roleRateFromGlobalStaff) && roleRateFromGlobalStaff > 0) {
      return roleRateFromGlobalStaff;
    }

    return 0;
  };

  console.log("Roles by Department:", rolesByDepartment);

  // Calculate total available hours for each role across all departments (for hiring stats)
  const totalHoursAvailableByRole = {};
  Object.entries(rolesByDepartment).forEach(([dept, deptRoles]) => {
    Object.entries(deptRoles).forEach(([deptRoleKey, roleInfo]) => {
      const role = roleInfo.role;
      const hourlyRate = roleInfo.hourlyRate;
      const left = departmentBudgetLeft[dept] || 0;
      const hoursAvailable = hourlyRate > 0 && left > 0 ? Math.floor(left / hourlyRate) : 0;
      if (!totalHoursAvailableByRole[role]) totalHoursAvailableByRole[role] = 0;
      totalHoursAvailableByRole[role] += hoursAvailable;
    });
  });

  // Calculate hiring potential for each unique role with department-level analysis
  const hiringStats = allUniqueRoles.map((role) => {
    const departmentBreakdown = allDepartmentNames.map((departmentName) => {
      const budgetLeft = Number(departmentBudgetLeft[departmentName]) || 0;
      const hourlyRate = getRoleRateForDepartment(role, departmentName);
      const estimatedHours = hourlyRate > 0 && budgetLeft > 0 ? Math.floor(budgetLeft / hourlyRate) : 0;

      return {
        departmentName,
        budgetLeft,
        hourlyRate,
        estimatedHours,
        fullTimeEquivalent: Math.floor(estimatedHours / 35),
        partTimeEquivalent: Math.floor(estimatedHours / 20),
      };
    });

    const totalHours = departmentBreakdown.reduce((sum, row) => sum + row.estimatedHours, 0);
    const fullTime = Math.floor(totalHours / 35);
    const partTime = Math.floor(totalHours / 20);
    const baseRate = getRoleRateForDepartment(role, allDepartmentNames[0] || '') || Number(roleRateByName[role]) || 0;

    return {
      role,
      hourlyRate: baseRate,
      totalHours,
      fullTime,
      partTime,
      departmentBreakdown,
      departmentsWithCapacity: departmentBreakdown.filter((row) => row.estimatedHours > 0).length,
    };
  });

  // --- Vacancy Modal Handlers ---
  const handleVacancyChange = (e) => {
    const { name, value } = e.target;
    setVacancyForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVacancySubmit = async (e) => {
    e.preventDefault();
    setVacancyError(false);
    setVacancySuccess(false);
    try {
      const response = await fetch("https://api.insightos.co.uk/api/newvacancy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          Role: vacancyForm.role,
          Jobs: parseInt(vacancyForm.jobs, 10),
          Description: vacancyForm.description,
          Skills: vacancyForm.skills,
          ClosingDate: vacancyForm.date,
          Assessment: vacancyForm.assessmentCode || null,
        }),
      });
      if (!response.ok) throw new Error("Failed to create vacancy");
      setVacancySuccess(true);
      setTimeout(() => setVacancySuccess(false), 2000);
      setVacancyModalOpen(false);
      window.location.reload()
      setVacancyForm({
        role: "",
        jobs: "",
        description: "",
        skills: "",
        date: "",
        assessmentCode: "",
      });
    } catch (error) {
      setVacancyError(true);
    }
  };

  // Get month names for dropdown
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className={shellBg}>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className={`${glassCard} mb-8`}>
          <div className="sm:flex sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
                Staff Costs & Budget Dashboard
              </h1>
              <p className="mt-3 text-lg text-gray-600">
                Monitor expenditure, manage budgets, and optimize resource allocation across departments.
              </p>
            </div>
            <div className="mt-4 sm:mt-0">
              <button
                onClick={() => setBugetModalOpen(true)}
                type="button"
                data-component="staff-costs"
                data-feature="open-budget-modal"
                className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
              >
                <CurrencyPoundIcon className="h-5 w-5 mr-2" />
                Manage Budgets
              </button>
            </div>
          </div>
        </div>

        {/* Expenditure Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Daily Expenditure Card */}
          <div className="rounded-2xl border border-emerald-100 bg-white/90 p-6 shadow-[0_16px_30px_rgba(16,185,129,0.15)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Today's Expenditure</p>
                <p className="text-3xl font-bold text-green-600">£{expenditure.daily?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-gray-500 mt-1">Based on actual shifts</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CalendarDaysIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          {/* Monthly Expenditure Card */}
          <div className="rounded-2xl border border-blue-100 bg-white/90 p-6 shadow-[0_16px_30px_rgba(59,130,246,0.14)]">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">Monthly Expenditure</p>
                <div className="flex items-center space-x-3 mt-2">
                  <select 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {monthNames.map((month, index) => (
                      <option key={index} value={index + 1}>{month}</option>
                    ))}
                  </select>
                </div>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  £{expenditure.monthly?.[selectedMonth]?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ChartBarIcon className="h-8 w-8 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Yearly Expenditure Card */}
          <div className="rounded-2xl border border-violet-100 bg-white/90 p-6 shadow-[0_16px_30px_rgba(124,58,237,0.14)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Year to Date</p>
                <p className="text-3xl font-bold text-purple-600">£{expenditure.yearly?.toFixed(2) || '0.00'}</p>
                <p className="text-sm text-gray-500 mt-1">{new Date().getFullYear()} total</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <ArrowUpOnSquareIcon className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

      {/* Budget Modal */}
      {bugetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Budget Management
              </h2>
            {reportError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error saving budgets!</h3>
                    <p className="text-sm text-red-700 mt-1">Please try again.</p>
                  </div>
                </div>
              </div>
            )}
            {reportSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L7.53 10.53a.75.75 0 00-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">Budgets saved successfully!</h3>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <label htmlFor="monthlyBudget" className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Budget (£)
                </label>
                <input
                  type="number"
                  name="monthlyBudget"
                  id="monthlyBudget"
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(parseFloat(e.target.value) || 0)}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  placeholder="Enter monthly budget"
                />
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Department Budget Allocation</h3>
                
                {/* Total Budget Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-sm text-gray-600">Total Monthly Budget</div>
                      <div className="text-lg font-bold text-blue-600">£{Number(monthlyBudget || 0).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Total Allocated</div>
                      <div className={`text-lg font-bold ${
                        (() => {
                          const total = Object.values(departmentBudgets).reduce((sum, val) => {
                            const numVal = Number(val) || 0;
                            return sum + numVal;
                          }, 0);
                          return total > Number(monthlyBudget || 0) ? 'text-red-600' : 'text-green-600';
                        })()
                      }`}>
                        £{(() => {
                          const total = Object.values(departmentBudgets).reduce((sum, val) => {
                            const numVal = Number(val) || 0;
                            return sum + numVal;
                          }, 0);
                          return Number(total).toLocaleString();
                        })()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Remaining</div>
                      <div className={`text-lg font-bold ${
                        (() => {
                          const total = Object.values(departmentBudgets).reduce((sum, val) => {
                            const numVal = Number(val) || 0;
                            return sum + numVal;
                          }, 0);
                          const remaining = Number(monthlyBudget || 0) - total;
                          return remaining < 0 ? 'text-red-600' : 'text-green-600';
                        })()
                      }`}>
                        £{(() => {
                          const total = Object.values(departmentBudgets).reduce((sum, val) => {
                            const numVal = Number(val) || 0;
                            return sum + numVal;
                          }, 0);
                          const remaining = Number(monthlyBudget || 0) - total;
                          return Number(remaining).toLocaleString();
                        })()}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const total = Object.values(departmentBudgets).reduce((sum, val) => {
                      const numVal = Number(val) || 0;
                      return sum + numVal;
                    }, 0);
                    const overAmount = total - Number(monthlyBudget || 0);
                    return overAmount > 0 ? (
                      <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-md">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                          </svg>
                          <span className="text-sm text-red-700 font-medium">
                            Warning: Total allocation exceeds monthly budget by £{Number(overAmount).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
                
                <div className="space-y-4">
                  {departments.map((dept) => {
                    const currentBudget = Number(departmentBudgets[dept.Name]) || 0;
                    const totalAllocated = Object.values(departmentBudgets).reduce((sum, val) => {
                      const numVal = Number(val) || 0;
                      return sum + numVal;
                    }, 0);
                    const percentageOfTotal = Number(monthlyBudget || 0) > 0 ? 
                      ((currentBudget / Number(monthlyBudget || 0)) * 100).toFixed(1) : "0.0";

                    return (
                      <div key={dept.Name} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3">
                          <label className="text-sm font-medium text-gray-700 mb-1 sm:mb-0">{dept.Name}</label>
                          <div className="flex flex-col items-end">
                            <span className="text-lg font-bold text-blue-600">£{currentBudget.toLocaleString()}</span>
                            <span className="text-xs text-gray-500">{percentageOfTotal}% of total</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={currentBudget || ''}
                            onChange={(e) => {
                              const rawValue = e.target.value;
                              
                              if (rawValue === '' || rawValue === null) {
                                setDepartmentBudgets((prev) => ({
                                  ...prev,
                                  [dept.Name]: 0,
                                }));
                                return;
                              }
                              
                              const numericValue = parseFloat(rawValue);
                              
                              if (!isNaN(numericValue) && numericValue >= 0) {
                                setDepartmentBudgets((prev) => ({
                                  ...prev,
                                  [dept.Name]: numericValue,
                                }));
                              }
                            }}
                            className={`w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              totalAllocated > Number(monthlyBudget || 0) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="0"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>Current: £{currentBudget.toLocaleString()}</span>
                            <span>{percentageOfTotal}% of budget</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setBugetModalOpen(false)}
                data-component="staff-costs"
                data-feature="cancel-budget-modal"
                className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setReportError(false);
                  setReportSuccess(false);
                  try {
                    const response = await fetch("https://api.insightos.co.uk/api/teambudgets", {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                      },
                      credentials: "include",
                      body: JSON.stringify({
                        monthlyBudget,
                        departmentBudgets,
                        daysOpen: openDaysPerWeek,
                      }),
                    });
                    if (!response.ok) throw new Error("Failed to save budgets");
                    setReportSuccess(true);
                    setTimeout(() => setReportSuccess(false), 2000);
                    setBugetModalOpen(false);
                    fetchDepartments();
                  } catch (error) {
                    setReportError(true);
                  }
                }}
                data-component="staff-costs"
                data-feature="save-budgets"
                className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500"
              >
                Save Budgets
              </button>
            </div>
            </div>
          </div>
      )}

        {/* Budget Distribution Chart */}
        <div className={`${glassCard} mb-8`}>
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
            Budget Distribution
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <VictoryPie
                  data={(() => {
                    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                    const totalAllocated = Object.values(departmentBudgets).reduce((sum, val) => sum + (val || 0), 0);
                    const chartData = departments.map((dept, index) => ({
                      x: dept.Name,
                      y: departmentBudgets[dept.Name] || 0,
                      fill: COLORS[index % COLORS.length]
                    })).filter(item => item.y > 0);
                    
                    // Add unallocated budget if any
                    const unallocated = monthlyBudget - totalAllocated;
                    if (unallocated > 0) {
                      chartData.push({
                        x: 'Unallocated',
                        y: unallocated,
                        fill: '#9CA3AF'
                      });
                    }
                    
                    return chartData;
                  })()}
                  width={300}
                  height={300}
                  innerRadius={60}
                  labelComponent={<VictoryTooltip />}
                  labelRadius={({ innerRadius }) => innerRadius + 40 }
                  colorScale={(() => {
                    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                    const totalAllocated = Object.values(departmentBudgets).reduce((sum, val) => sum + (val || 0), 0);
                    const colors = departments.map((dept, index) => COLORS[index % COLORS.length]);
                    
                    const unallocated = monthlyBudget - totalAllocated;
                    if (unallocated > 0) {
                      colors.push('#9CA3AF');
                    }
                    
                    return colors;
                  })()}
                  containerComponent={<VictoryContainer responsive={false}/>}
                />
              </div>
            </div>
            
            {/* Legend */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">Department Breakdown</h3>
              {departments.map((dept, index) => {
                const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                const budget = departmentBudgets[dept.Name] || 0;
                const totalAllocated = Object.values(departmentBudgets).reduce((sum, val) => sum + (val || 0), 0);
                const percentage = totalAllocated > 0 ? ((budget / totalAllocated) * 100).toFixed(1) : 0;
                
                return budget > 0 ? (
                  <div key={dept.Name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-3" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-sm font-medium text-gray-700">{dept.Name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">£{budget.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{percentage}%</div>
                    </div>
                  </div>
                ) : null;
              })}
              
              {(() => {
                const totalAllocated = Object.values(departmentBudgets).reduce((sum, val) => sum + (val || 0), 0);
                const unallocated = monthlyBudget - totalAllocated;
                return unallocated > 0 ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full mr-3 bg-gray-400"></div>
                      <span className="text-sm font-medium text-gray-700">Unallocated</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900">£{unallocated.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{((unallocated / monthlyBudget) * 100).toFixed(1)}%</div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        </div>

        {/* Department Budgets Table */}
        <div className={`${glassCard} mb-8`}>
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <CurrencyPoundIcon className="h-6 w-6 text-blue-600 mr-2" />
            Department Budget Analysis
          </h2>
          <div className={`overflow-x-auto ${tableShell}`}>
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100/90">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Budget</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Spend</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Average</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yearly Projection</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {departments.map((dept) => {
                  const name = dept.Name;
                  const budget = departmentBudgets[name] || 0;
                  const spend = departmentCosts[name] || 0;
                  const avgDaily = departmentAvgDaily[name] || 0;
                  const yearly = departmentYearly[name] || 0;
                  const left = departmentBudgetLeft[name] || 0;
                  const status = left >= 0 ? "In Budget" : "Over Budget";
                  const statusColor = left >= 0 ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50";
                  return (
                    <tr key={name} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">£{budget.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">£{spend.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">£{avgDaily.toFixed(2)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">£{yearly.toLocaleString()}</td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${getBudgetColor(left)}`}>
                        £{Math.abs(left).toLocaleString()}{left < 0 ? ' over' : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Role-based Hours Table */}
        <div className={`${glassCard} mb-8`}>
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <ChartBarIcon className="h-6 w-6 text-blue-600 mr-2" />
            Role Utilisation Analysis
          </h2>
          <div className={`overflow-x-auto ${tableShell}`}>
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100/90">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hourly Rate</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Remaining</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours Available</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {Object.entries(rolesByDepartment).map(([dept, deptRoles]) =>
                  Object.entries(deptRoles).map(([deptRoleKey, roleInfo]) => {
                    const left = departmentBudgetLeft[dept] || 0;
                    const hoursAvailable = roleInfo.hourlyRate > 0 && left > 0 ? Math.floor(left / roleInfo.hourlyRate) : 0;
                    console.log(`DEBUG - Role: ${dept} ${roleInfo.role}, Left: ${left}, HourlyRate: ${roleInfo.hourlyRate}, HoursAvailable: ${hoursAvailable}, DeptRoleKey: ${deptRoleKey}`);
                    console.log(`DEBUG - Department Budget Left for ${dept}:`, departmentBudgetLeft[dept]);
                    return (
                      <tr key={deptRoleKey} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{dept}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{roleInfo.role}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">£{roleInfo.hourlyRate.toFixed(2)}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${getBudgetColor(left)}`}>
                          £{left.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${hoursAvailable > 0 ? 'text-green-600 bg-green-50' : 'text-gray-500 bg-gray-50'}`}>
                            {hoursAvailable > 0 ? hoursAvailable : 0} hrs
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Company-wide Roles and Hiring Grid */}
        <div className={glassCard}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <ArrowUpOnSquareIcon className="h-6 w-6 text-blue-600 mr-2" />
              Hiring Potential Analysis
            </h2>
            <button
              onClick={() => setVacancyModalOpen(true)}
              data-component="staff-costs"
              data-feature="open-vacancy-modal"
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-green-700 rounded-lg shadow-lg hover:from-green-700 hover:to-green-800 transition-all duration-200"
            >
              Create Vacancy
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {hiringStats.map((stat) => (
              <div
                key={stat.role}
                className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {stat.role.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{stat.role}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Hourly Rate:</span>
                        <span className="font-semibold text-gray-900">£{Number(stat.hourlyRate || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estimated Hours (Total):</span>
                        <span className="font-semibold text-blue-600">{stat.totalHours}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Departments with Capacity:</span>
                        <span className="font-semibold text-indigo-700">{stat.departmentsWithCapacity}</span>
                      </div>
                      <div className="pt-2 border-t border-blue-200">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">Full-Time (35h):</span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                            {stat.fullTime} staff
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Part-Time (20h):</span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                            {stat.partTime} staff
                          </span>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-indigo-200">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-2">
                          Estimated Hiring Hours By Department
                        </p>
                        <div className="max-h-48 overflow-y-auto rounded-lg border border-indigo-100 bg-white/80">
                          {stat.departmentBreakdown.map((row) => (
                            <div
                              key={`${stat.role}-${row.departmentName}`}
                              className="grid grid-cols-5 gap-2 px-3 py-2 text-xs border-b border-indigo-50 last:border-b-0"
                            >
                              <span className="col-span-2 font-medium text-gray-700">{row.departmentName}</span>
                              <span className="text-gray-500">£{Number(row.hourlyRate || 0).toFixed(2)}/h</span>
                              <span className="font-semibold text-blue-700">{row.estimatedHours}h</span>
                              <span className="text-gray-600 text-right">{row.fullTimeEquivalent} FT / {row.partTimeEquivalent} PT</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* New Vacancy Modal */}
        {vacancyModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Vacancy</h2>
                {vacancyError && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error creating vacancy!</h3>
                        <p className="text-sm text-red-700 mt-1">Please try again.</p>
                      </div>
                    </div>
                  </div>
                )}
                {vacancySuccess && (
                  <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L7.53 10.53a.75.75 0 00-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Vacancy created successfully!</h3>
                      </div>
                    </div>
                  </div>
                )}
                <form onSubmit={handleVacancySubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="role">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={vacancyForm.role}
                      onChange={handleVacancyChange}
                      required
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select a role</option>
                      {allUniqueRoles.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="jobs">
                      Number of Positions
                    </label>
                    <input
                      type="number"
                      id="jobs"
                      name="jobs"
                      min={1}
                      required
                      value={vacancyForm.jobs}
                      onChange={handleVacancyChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Number of positions"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="description">
                      Job Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      value={vacancyForm.description}
                      onChange={handleVacancyChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Describe the role and responsibilities"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="skills">
                      Required Skills
                    </label>
                    <input
                      type="text"
                      id="skills"
                      name="skills"
                      required
                      value={vacancyForm.skills}
                      onChange={handleVacancyChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g. teamwork, communication, problem-solving"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="assessmentCode">
                      Assessment (type - role)
                    </label>
                    <select
                      id="assessmentCode"
                      name="assessmentCode"
                      value={vacancyForm.assessmentCode}
                      onChange={handleVacancyChange}
                      disabled={assessmentsLoading || (!assessmentsLoading && assessments.length === 0)}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                    >
                      <option value="">
                        {assessmentsLoading
                          ? "Loading assessments..."
                          : assessments.length > 0
                            ? "Select an assessment"
                            : "No assessments available"}
                      </option>
                      {assessments.map((assessment) => {
                        const typeLabel = assessment.type || "General";
                        const roleLabel = assessment.role || "Unassigned";
                        return (
                          <option key={assessment.assessmentCode} value={assessment.assessmentCode}>
                            {`${typeLabel} - ${roleLabel}`}
                          </option>
                        );
                      })}
                    </select>
                    {assessmentsError && (
                      <p className="mt-2 text-sm text-red-600">{assessmentsError}</p>
                    )}
                    {!assessmentsLoading && assessments.length === 0 && !assessmentsError && (
                      <p className="mt-2 text-sm text-gray-500">
                        Create an assessment in the Assessments tab to link it here.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="date">
                      Closing Date
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      required
                      value={vacancyForm.date}
                      onChange={handleVacancyChange}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  

                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setVacancyModalOpen(false)}
                      data-component="staff-costs"
                      data-feature="cancel-vacancy-modal"
                      className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      data-component="staff-costs"
                      data-feature="submit-vacancy"
                      className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500"
                    >
                      Create Vacancy
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
